import { environment } from '@energy-stacks/feature-config';
import { createBaseQuery, Pageable } from '@energy-stacks/shared';
import { createApi } from '@reduxjs/toolkit/query/react';
import { businessAccountsNormalizer } from './normalizers/businessAccountsNormalizer';
import { BusinessAccountModel } from './businessAccountModel';
import { businessAccountDetailsNormalizer } from './normalizers/businessAccountDetailsNormalizer';
import { BusinessAccountDetailsModel } from './businessAccountDetailsModel';
import { getDataByPlantId } from '@energy-stacks/fleet/shared';
import { BusinessAccountDto } from './businessAccountDto';
import {
  BusinessAccountsTableSortOrderModel,
  mapBusinessAccountsTableSortIdToPayload,
} from './mapBusinessAccountsTableSortIdToPayload';
import { PlantModel } from './plantModel';
import { PlantDto } from './plantDto';

export type GetBusinessAccountsRequestBody = {
  searchValue?: string;
  page: number;
  size: number;
  sort?: BusinessAccountsTableSortOrderModel;
};

export type BusinessAccountsPageModel = {
  businessAccounts?: BusinessAccountModel[];
  totalElements: number;
  totalPages: number;
  last: boolean;
  pageable: Pageable;
};

export interface BusinessAccountsPageDto {
  totalPages?: number;
  totalElements?: number;
  content?: BusinessAccountDto[];
  last: boolean;
  pageable: Pageable;
}

export const businessAccountsApi = createApi({
  reducerPath: 'business-accounts',
  baseQuery: createBaseQuery(`${environment.ocppServiceUrl}/business-accounts`),
  tagTypes: ['BusinessAccounts', 'PaginatedBusinessAccounts'],
  endpoints: (builder) => ({
    getPlantIds: builder.query<string[], void>({
      query: () => '/plants/depotIds',
    }),
    getPlant: builder.query<PlantModel, string>({
      query: (plantId) => `/plants/${plantId}/address`,
      transformResponse: (plantDto: PlantDto) => {
        return {
          id: plantDto.depotId,
          geoLocation: {
            latitude: plantDto.geoLocation?.latitude ?? '',
            longitude: plantDto.geoLocation?.longitude ?? '',
          },
        };
      },
    }),
    getBusinessAccounts: builder.query<BusinessAccountModel[], void>({
      queryFn: async (_, api, _extraArgs, baseQuery) => {
        return getDataByPlantId({
          api,
          baseQuery,
          url: '',
          transformResponse: businessAccountsNormalizer,
        });
      },
      keepUnusedDataFor: 0,
      providesTags: ['BusinessAccounts'],
    }),
    getPaginatedBusinessAccounts: builder.query<
      BusinessAccountsPageModel,
      GetBusinessAccountsRequestBody
    >({
      queryFn: async (params, api, _extraArgs, baseQuery) => {
        return getDataByPlantId({
          api,
          baseQuery,
          url: '/search',
          transformResponse: (
            businessAccountsPageDto: BusinessAccountsPageDto
          ) => {
            return {
              totalElements: businessAccountsPageDto.totalElements ?? 0,
              totalPages: businessAccountsPageDto.totalPages ?? 0,
              businessAccounts: businessAccountsNormalizer(
                businessAccountsPageDto.content ?? []
              ),
              last: businessAccountsPageDto.last,
              pageable: businessAccountsPageDto.pageable,
            } satisfies BusinessAccountsPageModel;
          },
          params: {
            ...params,
            sort: params?.sort
              ? mapBusinessAccountsTableSortIdToPayload(params.sort)
              : undefined,
          },
        });
      },
      keepUnusedDataFor: 0,
      providesTags: ['PaginatedBusinessAccounts'],
    }),
    getBusinessAccountDetails: builder.query<
      BusinessAccountDetailsModel,
      string
    >({
      query: (businessAccountId) => `/${businessAccountId}`,
      transformResponse: businessAccountDetailsNormalizer,
    }),
  }),
});

export const {
  useGetPlantIdsQuery,
  useGetPlantQuery,
  useGetPaginatedBusinessAccountsQuery,
  useGetBusinessAccountsQuery,
  useGetBusinessAccountDetailsQuery,
  usePrefetch,
} = businessAccountsApi;
